import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeftLong, faRightLong } from '@fortawesome/free-solid-svg-icons';
import { useEffect } from 'react';


const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
`;

const ResultsInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary, #6b7280);
`;

const PaginationButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color, #e5e7eb);
  background-color: var(--bg-secondary, #f9fafb);
  color: var(--text-primary, #111827);
  border-radius: 0.375rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--bg-hover, #f3f4f6);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-color, #3b82f6);
  }
`;

const PageNumbersContainer = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const PageNumberButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color, #e5e7eb);
  background-color: ${props => props.isActive ? 'var(--primary-color, #3b82f6)' : 'var(--bg-secondary, #f9fafb)'};
  color: ${props => props.isActive ? 'white' : 'var(--text-primary, #111827)'};
  border-radius: 0.375rem;
  cursor: pointer;
  min-width: 2.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.isActive ? 'var(--primary-color, #3b82f6)' : 'var(--bg-hover, #f3f4f6)'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-color, #3b82f6);
  }
`;

const Ellipsis = styled.span`
  padding: 0.5rem 0.25rem;
  color: var(--text-secondary, #6b7280);
`;

// Pagination Component
const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalCount,
  setTotalPages,
  setCurrentPage,
  pageSize = 10
}) => {
  const itemsPerPage = pageSize;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;


  useEffect(() => {
    const totalPageCount = Math.ceil(totalCount / itemsPerPage);
    setTotalPages(totalPageCount);
  }, [totalCount, itemsPerPage]);

  if (totalPages <= 1) return null;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };
;

  const renderPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
      // Show first page, last page, current page, and pages around current
      const showPage = page === 1 || page === totalPages || 
                     (page >= currentPage - 1 && page <= currentPage + 1);
      
      if (!showPage) {
        // Show ellipsis
        if (page === 2 && currentPage > 4) {
          return <Ellipsis key={page}>...</Ellipsis>;
        }
        if (page === totalPages - 1 && currentPage < totalPages - 3) {
          return <Ellipsis key={page}>...</Ellipsis>;
        }
        return null;
      }

      return (
        <PageNumberButton
          key={page}
          isActive={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </PageNumberButton>
      );
    });
  };

  return (
    <PaginationContainer data-testid="pagination">
      <ResultsInfo>
        Showing {startIndex + 1} to {endIndex > totalCount ? totalCount : endIndex} of {totalCount} results
      </ResultsInfo>

      <PaginationButtonsContainer>
        <PaginationButton
          onClick={handlePrevious}
          disabled={currentPage === 1}
        >
          <FontAwesomeIcon icon={faLeftLong} size="sm" /> 
          Previous
        </PaginationButton>

        <PageNumbersContainer>
          {renderPageNumbers()}
        </PageNumbersContainer>

        <PaginationButton
          onClick={handleNext}
          disabled={currentPage === totalPages}
        >
          Next
          <FontAwesomeIcon icon={faRightLong} size="sm" />
        </PaginationButton>
      </PaginationButtonsContainer>
    </PaginationContainer>
  );
};

export default Pagination